/**
 * 会话生命周期 composable。
 *
 * <p>职责：</p>
 * <ul>
 *   <li>{@link reset} - 切到空白草稿；在途流式会挂起在原会话，不 abort</li>
 *   <li>{@link setSession} - 切换 sessionId 并清空消息/错误/待办</li>
 *   <li>{@link clearSessionCache} - 删除某 session 的消息缓存与队列条目</li>
 *   <li>{@link attachToSession} - 切换侧边栏会话时加载服务端历史</li>
 *   <li>{@link ensureSessionIdBeforeMaterialUpload} - 首次上传附件前生成 client sessionId</li>
 *   <li>{@link prepareSessionForRecall} - 撤回前打断流式并清空挂起态</li>
 *   <li>{@link recallUserMessageTurn} - 撤回用户消息及其后续回合</li>
 * </ul>
 *
 * <p>本 composable 依赖主 store 注入的 {@link AiChatContext}，不持有自己的状态。</p>
 */
import {
  aiChatApi,
  type AiV2PendingActionView,
  type AiRecallUserMessageResponse,
  type AiStreamStateView
} from '@/api/aiChat'
import { useAiSessionsStore } from '@/stores/aiSessions'
import { cancelSseV2Stream, fetchSseV2Stream } from '@/api/aiChatV2'
import { isOpenUserAction } from './sessionTask'
import type { AiChatContext } from './context'
import { coalesceServerMessagesForUi } from './messages'
import { parseNumericUserMessageId, resumableReasonFromPhase, type AiUiMessage } from './types'
import type { CancellableAbort } from './context'
import {
  buildStreamBridge,
  createPendingAssistantMessage,
  createStreamAccumulator,
  settleStream,
  STREAM_INTERRUPTED_CONTINUE_HINT,
  withOwnerMessages
} from './streamRuntime'
import { createCoalescedDispatcherForBridge } from './streamCoalescer'

export function useSessionLifecycle(ctx: AiChatContext): {
  reset: () => void
  resetAllState: () => void
  setSession: (id: string | null) => void
  clearSessionCache: (targetSessionId: string) => void
  attachToSession: (clientSessionId: string) => Promise<void>
  ensureSessionIdBeforeMaterialUpload: () => void
  prepareSessionForRecall: () => void
  recallUserMessageTurn: (userMessageId: number) => Promise<AiRecallUserMessageResponse>
  restoreV2PendingActions: (targetSessionId: string) => Promise<void>
} {
  const {
    sessionId,
    messages,
    sending,
    lastError,
    recoverableStreamState,
    resumableStop,
    confirmationHttpPending,
    messagesBySession,
    userActionQueue,
    clearUserActionQueue,
    enqueueUserAction,
    resetQuestionState,
    streamsBySession,
    reconcileStaleStreamMessages
  } = ctx

  function reset(): void {
    const prevSid = sessionId.value
    if (prevSid) {
      messagesBySession.set(prevSid, [...messages.value])
    }
    // 切到空白草稿：在途流保留在原会话继续跑（按会话分桶，无需挂起/恢复视图状态）。
    sessionId.value = null
    messages.value = []
    lastError.value = null
    recoverableStreamState.value = null
    confirmationHttpPending.value = false
  }

  /**
   * 无条件清空全部 AI 对话状态（含跨会话消息快照缓存），用于登出 / 切换租户。
   *
   * <p>与 {@link reset} 的区别：</p>
   * <ul>
   *   <li>不因流式中（sending=true）而跳过——切换租户必须强清；</li>
   *   <li>额外清空 {@link messagesBySession} 跨会话缓存，否则上一个租户的消息内容
   *       会在切回同 sessionId 时被直接复用（跨租户内容泄漏）。</li>
   * </ul>
   */
  function resetAllState(): void {
    // 先中止全部在途流，避免清空后仍向 messages 写入残留数据。
    for (const [, ctrl] of streamsBySession.entries()) {
      ctrl._userCancelled = true
      if (ctrl._runId) {
        void cancelSseV2Stream(ctrl._runId).catch(() => undefined)
      }
      try {
        ctrl.abort()
      } catch {
        /* AbortController 可能已触发，忽略 */
      }
    }
    streamsBySession.clear()
    sessionId.value = null
    messages.value = []
    lastError.value = null
    clearUserActionQueue()
    recoverableStreamState.value = null
    confirmationHttpPending.value = false
    messagesBySession.clear()
  }

  function clearSessionCache(targetSessionId: string): void {
    messagesBySession.delete(targetSessionId)
    userActionQueue.value = userActionQueue.value.filter(
      (x) => x.conversationSessionId !== targetSessionId
    )
  }

  function setSession(id: string | null): void {
    if (id !== sessionId.value) {
      sessionId.value = id
      messages.value = []
      lastError.value = null
      clearUserActionQueue()
      recoverableStreamState.value = null
    }
  }

  async function restoreV2PendingActions(targetSessionId: string): Promise<void> {
    try {
      const response = await aiChatApi.getV2PendingActions(targetSessionId)
      const actions = response.data?.pendingActions ?? []
      if (sessionId.value !== targetSessionId || sending.value || confirmationHttpPending.value) {
        return
      }
      const beforeQuestionIds = userActionQueue.value
        .filter((item) => item.type === 'question' && item.conversationSessionId === targetSessionId)
        .map((item) => item.id)
      userActionQueue.value = userActionQueue.value.filter(
        (item) => item.conversationSessionId !== targetSessionId
      )
      // 先清后入队在同一同步块内完成，队列清空不会被 watcher 观察到。
      // 多问题进度只在"问题集合发生变化"时整体重置（历史事故：残留索引跳题）；
      // 同 id 重入队（事件触发的权威快照重拉）必须保留用户答题中暂存的答案与进度
      // （race-review S2：否则 history_updated 一到，用户已 staged 的答案全丢）。
      for (const action of actions) {
        enqueueRestoredV2Action(action, targetSessionId)
      }
      const afterQuestionIds = userActionQueue.value
        .filter((item) => item.type === 'question' && item.conversationSessionId === targetSessionId)
        .map((item) => item.id)
      if (beforeQuestionIds.length !== afterQuestionIds.length
        || beforeQuestionIds.some((id, i) => id !== afterQuestionIds[i])) {
        resetQuestionState()
      }
    } catch {
      // Message history remains usable when pending recovery is temporarily unavailable.
    }
  }

  function releaseRunningIfIdle(targetSessionId: string): void {
    if (streamsBySession.has(targetSessionId)) {
      return
    }
    if (userActionQueue.value.some(
      (item) => item.conversationSessionId === targetSessionId && isOpenUserAction(item)
    )) {
      return
    }
    useAiSessionsStore().markEnded(targetSessionId)
  }

  function enqueueRestoredV2Action(action: AiV2PendingActionView, targetSessionId: string): void {
    if (!action.pendingId) {
      return
    }
    if (action.kind === 'QUESTION') {
      enqueueUserAction({
        type: 'question',
        id: action.pendingId,
        toolCallId: action.toolCallId,
        conversationSessionId: targetSessionId,
        question: action.prompt ?? '请补充信息',
        options: Array.isArray(action.options) ? action.options : [],
        customOption: { id: 'custom', label: '自定义', maxLength: 200, placeholder: null },
        questionGroupId: action.questionGroupId ?? null,
        status: 'PENDING',
        expiresAt: action.expiresAt,
        error: null,
      })
      return
    }
    if (action.kind === 'CONFIRMATION') {
      enqueueUserAction({
        type: 'confirmation',
        id: action.pendingId,
        toolCallId: action.toolCallId,
        conversationSessionId: targetSessionId,
        message: action.displayLabel ? `请确认：${action.displayLabel}` : '该操作需要你的确认',
        toolName: action.toolName,
        displayLabel: action.displayLabel,
        riskLevel: action.riskLevel,
        effectiveRisk: action.effectiveRisk,
        argumentsJson: action.argumentsJson,
        preview: action.preflightReport,
        preflightReport: action.preflightReport,
        requiresRiskAcknowledgement: Boolean(action.requiresRiskAcknowledgement),
        expiresAt: action.expiresAt,
        status: 'PENDING',
        error: null,
      })
    }
  }

  /** Reconnects a refreshed page to the same journaled run without sending the user message again. */
  function recoverRunningV2Stream(targetSessionId: string, state: AiStreamStateView): void {
    if (!state.streamRunId || streamsBySession.has(targetSessionId)) {
      return
    }
    const sessionsStore = useAiSessionsStore()
    const pendingMsg = createPendingAssistantMessage()
    messages.value = [...messages.value, pendingMsg]
    messagesBySession.set(targetSessionId, [...messages.value])

    const streamAcc = createStreamAccumulator()
    streamAcc.v2RunId = state.streamRunId
    streamAcc.ownerSessionId = targetSessionId
    streamAcc.registeredSessionId = targetSessionId
    const abortCtrl: CancellableAbort = new AbortController()
    abortCtrl._runId = state.streamRunId
    streamsBySession.register(targetSessionId, abortCtrl)
    lastError.value = null
    sessionsStore.markRunning(targetSessionId)

    const bridge = buildStreamBridge({
      messages,
      sessionId,
      userActionQueue,
      lastError,
      resumableStop,
      streamsBySession,
      aiPlanStore: ctx.aiPlanStore,
      messagesBySession
    }, {
      pendingMsgId: pendingMsg.id,
      pendingMsgTemplate: pendingMsg,
      sessionsStore,
      hadPersistedSession: true,
      abortCtrl,
      acc: streamAcc
    })

    void (async () => {
      // 静默重连窗口：回连失败不代表任务死了（后台仍在跑），先查状态再决定
      // 重试还是收尾；确认真死了才提示，杜绝"任务其实活着却报连接中断"的误报。
      const reconnectWindowMs = 30000
      const reconnectStartedAt = Date.now()
      let attempt = 0
      try {
        while (true) {
          let recoveryError: Error | null = null
          // 性能债修复(热点1-4降频)：恢复流同样合帧应用，交互/终态立即冲刷。
          const dispatcher = createCoalescedDispatcherForBridge(bridge)
          try {
            await fetchSseV2Stream(state.streamRunId, {
              fromSeq: attempt === 0 ? 1 : (streamAcc.v2LastSeq ?? 0) + 1,
              signal: abortCtrl.signal,
              onEvent: dispatcher.onEvent,
              onError: (error) => {
                recoveryError = error
              }
            })
            // 流结束后同步冲刷残余缓冲，再判定终态（幂等）。
            dispatcher.flush()
            if (streamAcc.doneProcessed || streamAcc.streamErrored) {
              return
            }
            throw recoveryError ?? new Error('恢复连接未收到服务端完成事件')
          } catch (_error) {
            dispatcher.flush()
            // 用户已主动停止或离开：不再重试也不提示。
            if (abortCtrl.signal.aborted || abortCtrl._userCancelled) {
              return
            }
            const stateNow = await probeStreamState(targetSessionId)
            if (stateNow?.status === 'RUNNING' && Date.now() - reconnectStartedAt < reconnectWindowMs) {
              // 任务仍在后台进行：气泡保持"正在连接模型…"占位，退避后重试。
              attempt += 1
              await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * attempt, 3000)))
              continue
            }
            settleReconnectGiveUp(targetSessionId, pendingMsg, stateNow)
            return
          }
        }
      } finally {
        if (sessionId.value === targetSessionId) {
          recoverableStreamState.value = null
        }
        settleStream({
          streamsBySession,
          userActionQueue
        }, targetSessionId, sessionsStore)
      }
    })()
  }

  /** 回连放弃时的收尾：清掉恢复气泡，按任务真实状态给出准确的提示（不再误报）。 */
  function settleReconnectGiveUp(
    targetSessionId: string,
    pendingMsg: AiUiMessage,
    stateNow: AiStreamStateView | null
  ): void {
    withOwnerMessages(messages, messagesBySession, sessionId.value, targetSessionId, (target) => {
      const idx = target.value.findIndex((m) => m.id === pendingMsg.id)
      if (idx < 0) {
        return
      }
      const prev = target.value[idx]
      const hasContent = Boolean(prev.content?.trim() || (prev.timeline?.length ?? 0) > 0)
      if (hasContent) {
        target.value.splice(idx, 1, { ...prev, pending: false })
      } else {
        target.value.splice(idx, 1)
      }
    })
    if (stateNow == null) {
      // 状态查询也失败（服务端暂不可达）：沿用断点续接引导。
      lastError.value = STREAM_INTERRUPTED_CONTINUE_HINT
    } else if (stateNow.recoverable && stateNow.recoveryAction === 'user_stop') {
      markLastAssistantStoppedByUser(targetSessionId)
    } else if (stateNow.recoverable) {
      lastError.value = STREAM_INTERRUPTED_CONTINUE_HINT
    } else if (stateNow.status === 'RUNNING') {
      // 仍在跑但实时连不上：如实告知后台继续，而不是谎报"连接中断"。
      lastError.value = '与任务的实时连接暂时不稳定，任务仍在后台进行；稍后重新进入该会话即可查看最新进度。'
    } else {
      lastError.value = STREAM_INTERRUPTED_CONTINUE_HINT
    }
  }

  /** 查询会话当前流状态（失败返回 null，不抛错）。 */
  async function probeStreamState(targetSessionId: string): Promise<AiStreamStateView | null> {
    try {
      return (await aiChatApi.getStreamState(targetSessionId)).data ?? null
    } catch {
      return null
    }
  }

  /** 在消息列表中给最后一条助手消息打上「手动终止输出」标记（重进会话回放用户停止状态）。 */
  function markLastAssistantStoppedByUser(targetSessionId: string): void {
    withOwnerMessages(messages, messagesBySession, sessionId.value, targetSessionId, (target) => {
      for (let i = target.value.length - 1; i >= 0; i--) {
        if (target.value[i].role === 'assistant') {
          const prev = target.value[i]
          target.value.splice(i, 1, { ...prev, stoppedByUser: true })
          break
        }
      }
    })
  }

  /** 切换侧边栏会话时加载服务端历史（无记录则为空数组）。 */
  async function attachToSession(clientSessionId: string): Promise<void> {
    const prevSid = sessionId.value
    // 读点保鲜：当前会话非空就先落存，再读写任何缓存。同时覆盖两种场景：
    // 切换保存（prevSid≠目标）与带活流重入同一会话（断连恢复路径，
    // 保证后续读到的缓存新鲜，未落库的流式增量不丢）。
    if (prevSid) {
      messagesBySession.set(prevSid, [...messages.value])
    }
    // 目标会话仍由本页持有在途流（并发放开：也可能只是多个在途会话之一）
    const targetOwnsLiveStream = streamsBySession.has(clientSessionId)
    const targetLiveCache = targetOwnsLiveStream
      ? messagesBySession.get(clientSessionId)
      : undefined
    sessionId.value = clientSessionId
    // 会话归属必须和可见消息原子切换。普通历史会话以服务端历史为准，加载期间
    // 先展示空视图，绝不能继续展示（或复用可能已被污染的）上一个会话缓存。
    // 只有仍由本页持有 SSE 的目标会话可以恢复本地缓存，因为其中可能包含尚未落库的增量。
    messages.value = targetLiveCache ? [...targetLiveCache] : []
    lastError.value = null
    resumableStop.value = null

    if (targetLiveCache) {
      await restoreV2PendingActions(clientSessionId)
      releaseRunningIfIdle(clientSessionId)
      return
    }

    let streamState: AiStreamStateView | null
    try {
      streamState = (await aiChatApi.getStreamState(clientSessionId)).data ?? null
    } catch {
      streamState = null
    }
    if (sessionId.value !== clientSessionId) {
      return
    }

    // 非当前页持有的活跃流一律拉取服务端历史。服务端是已结束/中断会话的权威来源，
    // 这也会自动修复升级前已被旧竞态污染的内存缓存。
    try {
      const res = await aiChatApi.listMessages(clientSessionId)
      if (sessionId.value !== clientSessionId) {
        return
      }
      const rows = res.data ?? []
      messages.value = coalesceServerMessagesForUi(rows)
      messagesBySession.set(clientSessionId, [...messages.value])
      const state = streamState ?? (await aiChatApi.getStreamState(clientSessionId)).data ?? null
      // 重建统一队列（仅移除当前会话的旧条目，然后重新注入）
      userActionQueue.value = userActionQueue.value.filter((x) => x.conversationSessionId !== clientSessionId)
      if (sessionId.value !== clientSessionId) {
        return
      }
      recoverableStreamState.value = null
      await restoreV2PendingActions(clientSessionId)
      if (state?.status === 'RUNNING' && state.streamRunId) {
        recoverRunningV2Stream(clientSessionId, state)
      } else if (state?.recoverable) {
        const sessionsStore = useAiSessionsStore()
        sessionsStore.markEnded(clientSessionId)
        reconcileStaleStreamMessages(clientSessionId)
        if (state.recoveryAction === 'user_stop') {
          // 用户主动停止：不是连接中断，不打断连提示；
          // 在最后一条 AI 回复的重新生成按钮旁显示「手动终止输出」状态。
          markLastAssistantStoppedByUser(clientSessionId)
        } else {
          // recoverable 恒为终态（运行已被安全停止），不存在可重连的活流：
          // 不提供“点击恢复”入口（点了只会重复探测终态），直接引导用户从断点续接。
          lastError.value = STREAM_INTERRUPTED_CONTINUE_HINT
        }
      } else {
        // 非运行中且非断连恢复：按最近 run 的终态判定可继续状态（刷新后恢复继续提示条）。
        const resumeReason = resumableReasonFromPhase(state?.phase ?? null)
        resumableStop.value = resumeReason ? { reason: resumeReason } : null
        releaseRunningIfIdle(clientSessionId)
      }
    } catch {
      if (sessionId.value !== clientSessionId) {
        return
      }
      messages.value = []
      userActionQueue.value = userActionQueue.value.filter((x) => x.conversationSessionId !== clientSessionId)
      recoverableStreamState.value = null
    }
  }

  /**
   * Materials are stored per (tenant, user, session). Upload and tool calls require a non-blank
   * sessionId before the first SSE returns one — mint a client id matching the server prefix
   * {@code ai-} + UUID so the first POST /api/ai/chat/messages/stream creates the same conversation row.
   */
  function ensureSessionIdBeforeMaterialUpload(): void {
    if (sessionId.value != null && sessionId.value.trim() !== '') {
      return
    }
    sessionId.value = `ai-${crypto.randomUUID()}`
  }

  /** 撤回前打断流式并清空挂起态，避免旧 SSE / 待确认 / 待提问污染下一回合。 */
  function prepareSessionForRecall(): void {
    const currentSid = sessionId.value?.trim() ?? ''
    const ctrl = currentSid ? streamsBySession.get(currentSid) : null
    if (ctrl) {
      try {
        ctrl._userCancelled = true
        if (ctrl._runId) {
          void cancelSseV2Stream(ctrl._runId).catch(() => undefined)
        }
        ctrl.abort()
      } catch {
        /* noop */
      }
    }
    lastError.value = null
    // 仅清空当前会话的待办条目，避免污染其它会话
    if (currentSid) {
      userActionQueue.value = userActionQueue.value.filter((x) => x.conversationSessionId !== currentSid)
    }
    recoverableStreamState.value = null
    confirmationHttpPending.value = false
  }

  async function recallUserMessageTurn(userMessageId: number): Promise<AiRecallUserMessageResponse> {
    const sid = sessionId.value?.trim() ?? ''
    if (!sid) {
      throw new Error('当前无会话')
    }
    prepareSessionForRecall()
    const res = await aiChatApi.recallUserMessage(sid, userMessageId)
    const data = res.data
    if (!data) {
      throw new Error('撤回失败')
    }
    const uix = messages.value.findIndex((m) => parseNumericUserMessageId(m) === userMessageId)
    if (uix >= 0) {
      messages.value.splice(uix, messages.value.length - uix)
    }
    return data
  }

  return {
    reset,
    resetAllState,
    setSession,
    clearSessionCache,
    attachToSession,
    ensureSessionIdBeforeMaterialUpload,
    prepareSessionForRecall,
    recallUserMessageTurn,
    restoreV2PendingActions
  }
}
